import { observable, runInAction } from "mobx"
import { agentDirectionsFor, START_DIRECTIONS } from "./catalog"
import type { AgentStack, ApiEntry, GuideDoc } from "./types"

// The generated payloads too big for a landing page's first paint: every
// guide's markdown (~2.9 MB), the API reference (~0.4 MB) and the search index
// built over both (~1.3 MB). Each sits in its own webpack chunk and loads the
// first time a page reads it, so an ad visitor on /integrations/<id> downloads
// none of them. The small contract files stay static in ./catalog.

// One chunk, loaded once. `current` is observable: an observer or computed
// that read it while it was still null re-runs when the chunk lands, and that
// read is what starts the load. `loaded` asks without starting anything.
class LazyChunk<T> {
  private readonly box = observable.box<T | null>(null, { deep: false })
  private pending: Promise<T> | null = null

  constructor(private readonly loader: () => Promise<T>) {}

  load = (): Promise<T> => {
    if (!this.pending) {
      this.pending = this.loader().then((value) => {
        runInAction(() => this.box.set(value))
        return value
      })
      // A chunk that failed (a deploy replaced it, the network dropped) is
      // fetched again on the next read rather than remembered as failed.
      this.pending.catch(() => {
        this.pending = null
      })
    }
    return this.pending
  }

  get current(): T | null {
    const value = this.box.get()
    if (value == null) void this.load().catch(() => undefined)
    return value
  }

  get loaded(): boolean {
    return this.box.get() != null
  }
}

export const guidesChunk = new LazyChunk(() =>
  import(/* webpackChunkName: "docs-guides" */ "./generated/guides.json").then(({ default: json }) => {
    const guides = json as GuideDoc[]
    return {
      bySlug: Object.fromEntries(guides.map((guide) => [guide.slug, guide])) as Record<string, GuideDoc>,
      // /guides is the index page itself, so it is never one of the cards on it.
      publicGuides: guides.filter((guide) => guide.slug !== "guides")
    }
  })
)

export const apiEntriesChunk = new LazyChunk(() =>
  import(/* webpackChunkName: "docs-api" */ "./generated/apiEntries.json").then(
    ({ default: json }) => json as ApiEntry[]
  )
)

// MiniSearch and the index it builds over the guides and the API entries.
export const searchChunk = new LazyChunk(() => import(/* webpackChunkName: "docs-search" */ "./search"))

// null while the guides chunk loads, and for a slug no guide has. Observers
// that call it re-render when the chunk lands.
export const guideBySlug = (slug: string | null | undefined): GuideDoc | null => {
  if (!slug) return null
  return guidesChunk.current?.bySlug[slug] ?? null
}

// The agent directions themselves, one stack at a time. Rails serves each
// stack's payload at its `path` from the same sync, byte for byte (the
// catalog's `bytes` is that file's size), so a landing page fetches ~20–45 KB
// for its own stack instead of a chunk carrying all of them.
const directionsText = new Map<AgentStack, string>()
const directionsPending = new Map<AgentStack, Promise<string>>()

export const loadAgentDirections = (stack: AgentStack): Promise<string> => {
  if (stack === "start") return Promise.resolve(START_DIRECTIONS.markdown)
  let pending = directionsPending.get(stack)
  if (!pending) {
    pending = fetch(agentDirectionsFor(stack).path, { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`agent directions ${stack}: HTTP ${response.status}`)
        return response.text()
      })
      .then((text) => {
        directionsText.set(stack, text)
        return text
      })
    pending.catch(() => directionsPending.delete(stack))
    directionsPending.set(stack, pending)
  }
  return pending
}

// The text, if it has already arrived — what lets a click copy it inside the
// gesture rather than after a network wait.
export const loadedAgentDirections = (stack: AgentStack): string | null =>
  stack === "start" ? START_DIRECTIONS.markdown : (directionsText.get(stack) ?? null)

export const prefetchAgentDirections = (stack: AgentStack): void => {
  void loadAgentDirections(stack).catch(() => undefined)
}
